import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import ReviewForm from "@/components/reviews/ReviewForm";

const AdminReviewEdit = () => {
  const { id: reviewId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isLoading: authLoading } = useAuth();

  const [review, setReview] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  // 認證檢查：未登入時重定向到登入頁
  useEffect(() => {
    if (!authLoading && !user) {
      console.log('AdminReviewEdit: User not authenticated, redirecting to login');
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  // 主要的資料獲取邏輯
  useEffect(() => {
    if (!reviewId) {
      console.error('AdminReviewEdit: No reviewId provided');
      navigate('/admin/reviews');
      return;
    }

    // 等待認證完成
    if (authLoading) {
      return;
    }

    // 確保使用者已登入
    if (!user) {
      return; // 會被上面的 useEffect 處理重定向
    }

    const fetchReview = async () => {
      console.log('AdminReviewEdit: Fetching review', { reviewId, userId: user.id });
      setLoading(true);
      setError("");

      try {
        // 管理員可以編輯任何評論
        const { data, error: fetchError } = await supabase
          .from('reviews')
          .select('*')
          .eq('id', reviewId)
          .maybeSingle();

        if (fetchError) {
          console.error("AdminReviewEdit: Database error:", fetchError);
          setError("載入評論失敗");
          return;
        }

        if (!data) {
          console.log('AdminReviewEdit: No review found');
          setError("找不到評論");
          return;
        }

        // 資料轉換和預設值處理
        const transformedReview = {
          ...data,
          // 確保座標格式正確
          coordinates: (() => {
            if (data.coordinates && typeof data.coordinates === 'object' && !Array.isArray(data.coordinates)) {
              const coords = data.coordinates as Record<string, any>;
              if (coords.lat && coords.lng) {
                return { lat: Number(coords.lat), lng: Number(coords.lng) };
              }
            }
            return { lat: 51.0447, lng: -114.0719 };
          })(),
          
          // 確保陣列欄位
          parking_information: Array.isArray(data.parking_information) ? data.parking_information : [],
          utilities_included: data.utilities_included || [],
          image_urls: data.image_urls || [],
          
          // 確保數字欄位
          bedrooms: data.bedrooms || 0,
          living_rooms: data.living_rooms || 0,
          bathrooms: data.bathrooms || 0,
          
          // 確保押金類型正確
          deposit_type: (data.deposit_type === 'amount' || data.deposit_type === 'months') 
            ? data.deposit_type 
            : 'amount',
            
          // 確保布林值
          pet_allowed: data.pet_allowed || false,
        };

        setReview(transformedReview);
        console.log('AdminReviewEdit: Review loaded successfully');

      } catch (error: any) {
        console.error("AdminReviewEdit: Unexpected error:", error);
        setError("發生意外錯誤");
      } finally {
        setLoading(false);
      }
    };

    fetchReview();
  }, [reviewId, user, authLoading]);

  // 載入超時處理
  useEffect(() => {
    if (!loading) return;

    const timeout = setTimeout(() => {
      console.warn('AdminReviewEdit: Loading timeout after 10 seconds');
      setLoading(false);
      setError("載入超時，請重新整理頁面");
    }, 10000);

    return () => clearTimeout(timeout);
  }, [loading]);

  // 錯誤發生時顯示 toast
  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "錯誤",
        description: error,
      });
    }
  }, [error, toast]);

  const handleGoBack = () => {
    navigate('/admin/reviews');
  };

  // Loading 狀態
  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex flex-col justify-center items-center h-48 space-y-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-muted-foreground">載入評論中...</p>
        </div>
      </div>
    );
  }

  // 錯誤狀態
  if (error || !review) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex flex-col justify-center items-center h-48 space-y-4">
          <p className="text-muted-foreground text-center">
            {error || '找不到評論'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/reviews')}>
              返回評論管理
            </Button>
            <Button variant="outline" onClick={() => window.location.reload()}>
              重試
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 成功載入評論，顯示編輯表單
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Button variant="ghost" onClick={handleGoBack} className="mb-4">
        <ArrowLeft className="mr-2 h-4 w-4" />
        返回評論管理
      </Button>
      
      <h1 className="text-2xl font-bold mb-4">編輯評論 (管理員)</h1>
      
      <ReviewForm 
        initialValues={review} 
        isEditing={true} 
        reviewId={reviewId!} 
        userId={review.user_id}
      />
    </div>
  );
};

export default AdminReviewEdit;